/**
 * Telefone e nome de empresa como a pessoa digita no onboarding (A109).
 *
 * Telefone brasileiro com DDD: o celular tem 11 dígitos e começa com 9 depois
 * do DDD — `(11) 9 8765-4321`, o formato do protótipo; o fixo tem 10 —
 * `(11) 3456-7890`. A máscara se monta a partir dos dígitos, então letra e
 * símbolo digitados somem, e o terceiro dígito decide qual dos dois formatos
 * (e quantos dígitos) vale.
 *
 * Nome de empresa: quem digita "padaria do joão" vê "Padaria do João". Só a
 * palavra toda minúscula muda; palavra com alguma maiúscula fica como veio —
 * "iFood", "XP", "IBM", e o nome todo em maiúsculas de quem copiou do CNPJ.
 */

/**
 * Os dígitos sem o que vem antes do DDD. Zero na frente sai sempre — DDD
 * nunca começa com 0, então é o 0 da operadora (ou um 0 digitado por
 * engano). O código do país sai quando sobram dígitos: o preenchimento
 * automático do navegador manda "+55 11 98765-4321". Com até 11 dígitos,
 * "55" é DDD (Rio Grande do Sul), e fica.
 */
function semPrefixo(valor: string | null | undefined): string {
  let d = String(valor ?? '').replace(/\D/g, '').replace(/^0+/, '');
  if (d.length > 11 && d.startsWith('55')) d = d.slice(2).replace(/^0+/, '');
  return d;
}

/** Quantos dígitos o número tem: 11 no celular (9 depois do DDD), 10 no fixo. */
const tamanhoDoNumero = (d: string) => (d[2] === '9' ? 11 : 10);

/** Os dígitos do telefone, até o tamanho do formato. */
export function digitosDoTelefone(valor: string | null | undefined): string {
  const d = semPrefixo(valor);
  return d.slice(0, tamanhoDoNumero(d));
}

/** A máscara, montada enquanto se digita: `(11`, `(11) 9 87`, `(11) 9 8765-4321`. */
export function formatarTelefone(valor: string | null | undefined): string {
  const d = digitosDoTelefone(valor);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;
  const ddd = `(${d.slice(0, 2)}) `;
  const resto = d.slice(2);
  if (resto[0] === '9') {
    // Celular: 9 · 4 · 4.
    if (resto.length <= 1) return ddd + resto;
    if (resto.length <= 5) return `${ddd}${resto[0]} ${resto.slice(1)}`;
    return `${ddd}${resto[0]} ${resto.slice(1, 5)}-${resto.slice(5)}`;
  }
  // Fixo: 4 · 4.
  if (resto.length <= 4) return ddd + resto;
  return `${ddd}${resto.slice(0, 4)}-${resto.slice(4)}`;
}

/** Completo e sem sobra: 11 dígitos começando com 9 depois do DDD (celular), ou 10 sem (fixo). */
export function telefoneBrasileiroValido(valor: string | null | undefined): boolean {
  const d = semPrefixo(valor);
  return d.length === tamanhoDoNumero(d);
}

/** Palavras que ficam minúsculas no meio do nome: "Padaria do João". */
const PARTICULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'em', 'na', 'no', 'nas', 'nos', 'a', 'o', 'para', 'com']);

/** Siglas de natureza jurídica, digitadas em minúscula. */
const SIGLAS: Record<string, string> = {
  ltda: 'Ltda',
  'ltda.': 'Ltda.',
  me: 'ME',
  epp: 'EPP',
  eireli: 'EIRELI',
  's/a': 'S/A',
  's.a.': 'S.A.',
  'sa': 'S.A.',
};

const comInicialMaiuscula = (parte: string) => (parte ? parte.charAt(0).toLocaleUpperCase('pt-BR') + parte.slice(1) : parte);

/**
 * O nome da empresa arrumado: espaços normais, e cada palavra toda minúscula
 * com a inicial maiúscula — menos as partículas no meio do nome. Hífen conta
 * como divisão ("coca-cola" → "Coca-Cola").
 */
export function nomeDeEmpresaFormatado(valor: string | null | undefined): string {
  const palavras = String(valor ?? '').trim().split(/\s+/).filter(Boolean);
  return palavras
    .map((palavra, i) => {
      if (palavra !== palavra.toLocaleLowerCase('pt-BR')) return palavra; // tem maiúscula: a pessoa quis assim
      if (SIGLAS[palavra] && i > 0) return SIGLAS[palavra];
      if (i > 0 && PARTICULAS.has(palavra)) return palavra;
      return palavra.split('-').map(comInicialMaiuscula).join('-');
    })
    .join(' ');
}
